/**
 * PatientSigninForm — Portal-Login fuer Patient:innen.
 * Extrahiert aus `pages/Login.tsx` (UX-Audit Phase 5).
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  patientSigninSchema,
  type PatientSigninInput,
} from '@/schemas/auth';

interface Props {
  onSubmit: (values: PatientSigninInput) => Promise<void>;
}

export function PatientSigninForm({ onSubmit }: Props) {
  const form = useForm<PatientSigninInput>({
    resolver: zodResolver(patientSigninSchema),
    defaultValues: { email: '', password: '' },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="text-xs text-muted-foreground">E-Mail</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="ihre@email.de"
                  className="bg-secondary border-border focus:ring-1 focus:ring-primary"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="text-xs text-muted-foreground">Passwort</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  className="bg-secondary border-border focus:ring-1 focus:ring-primary"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <p className="text-xs text-muted-foreground bg-secondary rounded-lg p-3 leading-relaxed">
          Sie benötigen eine Einladung von Ihrer Ernährungsfachkraft, um das Portal nutzen zu können.
        </p>
        <Button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl h-10"
        >
          {form.formState.isSubmitting && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          )}
          Anmelden
        </Button>
      </form>
    </Form>
  );
}
